import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { filterStates } from '../data-provider/states/normalize';
import type { StateFilters, States, StatesParams } from '../models';
import * as statesService from '../services/states.service';

export const useStates = (
  defaultFilters: Partial<StateFilters & StatesParams & { searchParams: { ids: string[] } }> = {},
) => {
  const { searchParams, CountryCode = '', ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<StateFilters>>(otherDefaultFilters);

  const cacheData = statesService.getAll(CountryCode);

  const updateFilters = useCallback((newFilters: Partial<StateFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const params = useMemo(() => ({ CountryCode }), [CountryCode]);

  const { data, isLoading, isError } = useQuery(
    statesService.statesQueryOptions(params, { enabled: Boolean(CountryCode) }),
  );

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const mergedMap: States['map'] = useMemo(() => {
    const map: States['map'] = {
      ...(data?.map ?? {}),
      ...(cacheData?.map ?? {}),
    };

    if (data && missingIds.length) {
      for (const id of missingIds) {
        const state = data.map[id];
        if (state) {
          map[id] = state;
        }
      }
    }

    return map;
  }, [data, cacheData?.map, missingIds]);

  const filteredData = useMemo(
    () => filterStates({ list: Object.values(mergedMap), map: mergedMap }, filters),
    [mergedMap, filters],
  );

  return {
    data: filteredData,
    isError,
    isLoading,
    updateFilters,
  };
};
